import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Loader2, TrendingUp, Package, Award, AlertTriangle, Factory, Users, DollarSign, RefreshCw, Zap, XCircle, CheckCircle2, PauseCircle } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, hint, color = "blue", testid }) {
  const colors = {
    blue: "bg-blue-500/10 text-blue-600 border-blue-200",
    emerald: "bg-emerald-500/10 text-emerald-600 border-emerald-200",
    amber: "bg-amber-500/10 text-amber-600 border-amber-200",
    rose: "bg-rose-500/10 text-rose-600 border-rose-200",
    indigo: "bg-indigo-500/10 text-indigo-600 border-indigo-200",
    slate: "bg-slate-500/10 text-slate-600 border-slate-200",
  }[color] || "bg-slate-100 text-slate-700";
  return (
    <Card className="overflow-hidden" data-testid={testid}>
      <CardContent className="pt-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-bold">{label}</p>
            <p className="text-3xl font-black mt-1 text-foreground">{value}</p>
            {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
          </div>
          <div className={`h-10 w-10 rounded-lg border flex items-center justify-center ${colors}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/executive/summary`);
      setData(r.data);
      setLastFetch(new Date());
    } catch (e) { showError(e, "Erro"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);  // refresh a cada 1 min
    return () => clearInterval(iv);
  }, []);

  if (!data && loading) return (
    <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" /></div>
  );
  if (!data) return null;

  return (
    <div className="p-6 space-y-6" data-testid="executive-dashboard">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-black text-slate-800">Dashboard Executivo</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Visão de gestão em tempo real · Actualizado {lastFetch?.toLocaleTimeString("pt-PT")}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-exec">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Alertas críticos no topo */}
      {data.alerts && data.alerts.length > 0 && (
        <div className="space-y-2" data-testid="exec-alerts">
          {data.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border-2 ${
              a.level === "critical" ? "bg-rose-50 border-rose-300 text-rose-800"
                : "bg-amber-50 border-amber-300 text-amber-800"
            }`}>
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <span className="font-semibold text-sm">{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* KPIs principais - grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Package} label="Produção Hoje" value={data.production_today.good.toLocaleString()}
          hint={`+ ${data.production_today.rejected} rejeitadas (${data.reject_rate_today}%)`}
          color="emerald" testid="kpi-prod-today" />
        <StatCard icon={TrendingUp} label="Produção Semana" value={data.production_week.good.toLocaleString()}
          hint={`${data.reject_rate_week}% rejeição`}
          color="blue" testid="kpi-prod-week" />
        <StatCard icon={Zap} label="OEE Médio" value={`${data.oee_average}%`}
          hint="Semana actual"
          color={data.oee_average > 75 ? "emerald" : data.oee_average > 55 ? "amber" : "rose"} testid="kpi-oee" />
        <StatCard icon={DollarSign} label="Valor Produzido Hoje" value={`€ ${data.value_produced_today.toLocaleString()}`}
          hint="Boas × preço unitário"
          color="indigo" testid="kpi-value" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Factory} label="Ordens em Curso" value={data.orders_running}
          hint={`${data.orders_pending} pendentes`}
          color="blue" testid="kpi-orders-running" />
        <StatCard icon={Factory} label="Máquinas a Produzir" value={data.machines_producing}
          hint="Esta semana"
          color="emerald" testid="kpi-machines" />
        <StatCard icon={XCircle} label="Rejeitadas Hoje" value={data.production_today.rejected.toLocaleString()}
          hint={`${data.reject_rate_today}% do total`}
          color="rose" testid="kpi-rejected" />
        <StatCard icon={Package} label="Registos Hoje" value={data.production_today.records}
          hint="Reports operadores"
          color="slate" testid="kpi-records" />
      </div>

      {/* Highlights: operador do dia, maquinas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Operador do Dia */}
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
              <Award className="h-4 w-4" />Operador do Dia
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.operator_of_day ? (
              <div className="flex items-center gap-3">
                {data.operator_of_day.photo_url ? (
                  <img src={data.operator_of_day.photo_url} alt="" className="h-16 w-16 rounded-full object-cover border-4 border-emerald-300" />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold text-2xl border-4 border-emerald-300">
                    {(data.operator_of_day.name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-bold text-lg text-slate-800">{data.operator_of_day.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{data.operator_of_day.user_code}</div>
                  <div className="text-emerald-700 font-bold mt-1">{data.operator_of_day.good_quantity.toLocaleString()} boas</div>
                </div>
              </div>
            ) : <div className="text-sm text-muted-foreground">Sem dados hoje</div>}
          </CardContent>
        </Card>

        {/* Máquina + Eficiente */}
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
              <CheckCircle2 className="h-4 w-4" />Máquina Mais Eficiente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.machine_top_efficient ? (
              <div>
                <div className="font-bold text-2xl text-blue-800">{data.machine_top_efficient.code}</div>
                <div className="text-xs text-slate-600">{data.machine_top_efficient.description}</div>
                <div className="text-blue-700 font-bold mt-2">{data.machine_top_efficient.good_quantity.toLocaleString()} boas / semana</div>
              </div>
            ) : <div className="text-sm text-muted-foreground">Sem dados</div>}
          </CardContent>
        </Card>

        {/* Máquina + Paragens */}
        <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-rose-700">
              <PauseCircle className="h-4 w-4" />Máquina Com Mais Paragens
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.machine_top_stops ? (
              <div>
                <div className="font-bold text-2xl text-rose-800">{data.machine_top_stops.code}</div>
                <div className="text-xs text-slate-600">{data.machine_top_stops.description}</div>
                <div className="text-rose-700 font-bold mt-2">
                  {data.machine_top_stops.stops} paragens · {data.machine_top_stops.total_min}min / semana
                </div>
              </div>
            ) : <div className="text-sm text-muted-foreground">Sem paragens</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
